import { useSelector } from 'react-redux';
import { Accordion } from 'react-accessible-accordion';
import slugify from 'slugify';

// components
import { CountrySwitchAccordionItem } from './CountrySwitchAccordionItem';
import { AccordionContent } from '../Accordion/AccordionContent';
import { Title } from '../Title/Title';
import { useIsMobile } from 'components/App/SizeProvider';

// types
import type { CountrySwitchPayload } from '../content-elements-payload';

// utils
import { TitleFormats } from 'components/ContentElements/Title/title.types';
import { useSectionId } from 'utils/hooks/use-section-id';
import { footerCountrySwitchSelector } from 'utils/selectors/globalsSelectors';

export interface CountrySwitchProps {
  region: CountrySwitchPayload['region'];
  title: string;
  subtitle: string;
  anchor: string;
  contentIndex: number;
  isMobile: boolean;
  titleInAnchor: boolean;
}

export function CountrySwitch({
  anchor,
  region,
  title,
  subtitle,
  contentIndex,
  titleInAnchor,
}: Readonly<CountrySwitchProps>) {
  const countrySwitch = useSelector(footerCountrySwitchSelector);
  const currentCountryId = countrySwitch?.countryFsId;
  const isMobile = useIsMobile();

  const includesCurrentLanguage = (item) =>
    item.country.some((country) => country.id === currentCountryId);

  const preOpenItems = region
    .filter(includesCurrentLanguage)
    .map((item, index) => `${index}-${slugify(item.name)}`);

  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);

  const handleClick = () => {
    setTimeout(() => {
      const openElement = document.querySelector(
        '.country-switch .c-accordion__title[aria-expanded="true"]',
      );
      if (openElement) {
        const headerHeight = document.getElementById('page-header')?.offsetHeight ?? 0;
        const top = openElement.getBoundingClientRect().top - headerHeight;
        window.scrollBy({
          top,
          left: 0,
          behavior: 'smooth',
        });
      }
    }, 0);
  };

  return (
    <div className="grid-container country-switch" id={sectionId}>
      <Title
        title={title}
        subtitle={subtitle}
        Format={TitleFormats.h2}
        titlePreviewId="#st_title"
        subtitlePreviewId="#st_subtitle"
        formatClassName="h3"
      />
      <Accordion className="c-accordion" allowZeroExpanded preExpanded={preOpenItems}>
        {region.map((item, index) => {
          const content = (
            <CountrySwitchAccordionItem countries={item.country} countryId={currentCountryId} />
          );
          return (
            <AccordionContent
              uuid={`${index}-${slugify(item.name)}`}
              index={index}
              title={item.name}
              content={content}
              key={`${index}-${item.name}`}
              handleClick={isMobile ? handleClick : undefined}
              sourceComponent="countrySwitch"
            />
          );
        })}
      </Accordion>
    </div>
  );
}
